import React, { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * A pinned child-page shortcut row at the top of the sidebar.
 *
 * As of iter-40 (plugin-era rework) every per-row action icon was
 * removed — no pencil, no pin, no trash, no grip. The row is just
 * `filename` + an optional indicator dot. All file-level actions (pin,
 * unpin, rename, delete) live in the panel toolbar at the top of the
 * sidebar and target the currently-selected file.
 */
export default function SortablePinnedChildRow({
    child,
    parent,
    active,
    onPickFile,
    onRename,
    renamingFileId = null,
    onRenameRequest,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: child.id });

    const renaming = renamingFileId === child.id;
    const setRenaming = (v) => {
        if (!onRenameRequest) return;
        onRenameRequest(v ? child.id : null);
    };

    const inputRef = useRef(null);
    useEffect(() => {
        if (renaming && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [renaming]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    const label =
        child.display_name || `Page ${child.page_number ?? ""}`.trim();

    return (
        <li
            ref={setNodeRef}
            style={style}
            className="group/item px-1"
            data-pinned="true"
            data-testid={`workspace-pinned-child-${child.id}`}
        >
            <div className="flex items-stretch gap-1">
                {/* Spacer that mirrors the expand/collapse chevron column on
                    regular rows so this row aligns cleanly with unpinned
                    siblings underneath. */}
                <div className="shrink-0 w-5" />
                <button
                    type="button"
                    onClick={() => {
                        if (renaming) return;
                        onPickFile && onPickFile();
                    }}
                    data-testid={`workspace-page-${child.id}`}
                    {...attributes}
                    {...(renaming ? {} : listeners)}
                    className={`flex-1 min-w-0 flex items-center gap-2 px-2 py-2 text-sm text-left rounded-sm transition-colors touch-none cursor-grab active:cursor-grabbing ${
                        active
                            ? "bg-[#007AFF]/15 text-white border-l-2 border-[#00A3FF]"
                            : "text-slate-300 hover:bg-[#1C253C] hover:text-white"
                    }`}
                >
                    <span
                        className={`w-4 h-4 shrink-0 rounded-sm border ${
                            active
                                ? "bg-[#00A3FF] border-[#00A3FF]"
                                : "border-slate-500"
                        }`}
                    />
                    {renaming ? (
                        <input
                            ref={inputRef}
                            type="text"
                            defaultValue={label}
                            placeholder={`Page ${child.page_number}`}
                            data-testid={`workspace-rename-input-${child.id}`}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={(e) => {
                                const v = e.target.value;
                                setRenaming(false);
                                if (
                                    (v || "").trim() !==
                                    (child.display_name || "").trim()
                                ) {
                                    onRename && onRename(child, v);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.target.blur();
                                } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setRenaming(false);
                                }
                            }}
                            className="min-w-0 flex-1 bg-[#0A1128] border border-[#00A3FF]/60 rounded-sm px-1 py-0.5 text-sm text-white outline-none focus:ring-1 focus:ring-[#00A3FF]/60"
                        />
                    ) : (
                        <span
                            className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                            title={`${label} · ${
                                parent?.original_filename || "pinned page"
                            } · press & hold to reorder`}
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setRenaming(true);
                            }}
                        >
                            {label}
                        </span>
                    )}
                    {/* Tiny blue dot — visual "pinned" indicator; no
                        interactivity (pin/unpin moved to the panel
                        toolbar). */}
                    <span
                        aria-hidden
                        className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#00A3FF]"
                        title="Pinned"
                    />
                </button>
            </div>
        </li>
    );
}
